/* eslint-disable camelcase */
import React from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useSelector } from 'react-redux'
import { orderBy } from 'lodash'
import i18n from 'simple-react-i18n'
import moment from 'moment'

import { getSandreLabel } from '../../../utils/StringUtil'
import { SANDRE } from '../../../referencial/constants/ReferencialConstants'
import { ProductionUnitShape } from '../../../productionUnit/shapes/ProductionUnitShapes'
import DtoDistributionUnit from '../../../distributionUnit/dto/DtoDistributionUnit'
import DtoQualitometer from '../../../quality/dto/DtoQualitometer'
import DtoPiezometer from '../../../piezometry/dto/DtoPiezometer'
import DtoHydrometricStation from '../../../hydrometry/dto/DtoHydrometricStation'
import DtoInstallation from '../../../installation/dto/installation/DtoInstallation'
import { getFullDate, getHour } from '../../../utils/DateUtil'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import { authorizeFilter } from 'utils/HabilitationUtil'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { Card, CardContent, Grid } from '@mui/material'
import useSandreList from 'utils/customHook/useSandreList'
import Input from 'components/forms/Input'
import { onChangeHour } from 'utils/FormUtils'
import Select from 'components/forms/Select'
import NumberField from 'components/forms/NumberField'
import StationDescriptionTable from './StationDescriptionTable'

const headers = ['startDate', 'endDate', 'description', 'condition', 'closing', 'contact', 'order']

const LithologyDialogContent = ({
    stateElement,
    onChangeElement = () => { },
}) => {
    const {
        contacts,
        accountUser,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const closingTypeList = useSandreList(SANDRE.ACCESSIBILITIES_TYPE_FERMETURE)
    const conditionTypeList = useSandreList(SANDRE.ACCESSIBILITIES_TYPE_CONDITION)

    return (
        <Card>
            <CardContent>
                <Grid container columnSpacing={1}>
                    <Grid size={6}>
                        <SimpleDatePicker
                            value={stateElement.startDate}
                            label={i18n.startDate}
                            onChange={v => onChangeElement({ startDate: v })}
                            max={stateElement.endDate}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={i18n.startHour}
                            value={getHour(stateElement.startDate)}
                            onChange={v => onChangeHour(v, v2 => onChangeElement({ startDate: v2 }), { max: stateElement.endDate }, stateElement.startDate)}
                        />
                    </Grid>
                    <Grid size={6}>
                        <SimpleDatePicker
                            value={stateElement.endDate}
                            label={i18n.endDate}
                            onChange={v => onChangeElement({ endDate: v })}
                            min={stateElement.startDate}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Input
                            title={i18n.endHour}
                            value={getHour(stateElement.endDate)}
                            onChange={v => onChangeHour(v, v2 => onChangeElement({ endDate: v2 }), { min: stateElement.startDate }, stateElement.endDate)}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Select
                            label={i18n.contact}
                            options={authorizeFilter(contacts, accountUser, stateElement.contactCode, 'contributor')}
                            keyValue='code'
                            keyLabel='name'
                            keyFilter={['name', 'socialReason']}
                            onChange={v => onChangeElement({ contactCode: v })}
                            value={stateElement.contactCode}
                        />
                    </Grid>
                    <Grid size={2}>
                        <NumberField
                            value={stateElement.order}
                            min={1}
                            title={i18n.order}
                            disabled={!!stateElement.endDate}
                            onChange={v => onChangeElement({ order: v })}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Select
                            label={i18n.conditionType}
                            options={conditionTypeList}
                            onChange={v => onChangeElement({ codeCondition: v })}
                            value={stateElement.codeCondition}
                            nullLabel='&nbsp;'
                        />
                    </Grid>
                    <Grid size={6}>
                        <Select
                            options={closingTypeList}
                            label={i18n.closingType}
                            onChange={v => onChangeElement({ closingCode: v })}
                            value={stateElement.closingCode}
                            nullLabel='&nbsp;'
                        />
                    </Grid>
                    <Grid size={12}>
                        <SimpleTextArea
                            title={i18n.description}
                            value={stateElement.description}
                            onChange={v => onChangeElement({ description: v })}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

LithologyDialogContent.propTypes = {
    stateElement: PropTypes.object,
    onChangeElement: PropTypes.func,
}

const StationAccessibilitiesPanel = ({
    station = {},
    readMode = false,
    onChange = () => { },
    onShowContact = () => { },
}) => {
    const {
        contacts,
        sandreCodes,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
        sandreCodes: store.ReferencialReducer.sandreCodes,
    }), shallowEqual)

    const now = moment().valueOf()

    return (
        <StationDescriptionTable
            station={station}
            readMode={readMode}
            onChange={onChange}
            keyList='link_accessibilities'
            formatFunction={access => {
                const contact = contacts.find(c => c.code === access.contactCode)
                return {
                    ...access,
                    contact: contact && contact.name,
                    condition: getSandreLabel(sandreCodes, SANDRE.ACCESSIBILITIES_TYPE_CONDITION, access.codeCondition),
                    closing: getSandreLabel(sandreCodes, SANDRE.ACCESSIBILITIES_TYPE_FERMETURE, access.closingCode),
                    startDate: getFullDate(access.startDate),
                    endDate: getFullDate(access.endDate),
                    color: access.endDate < now ? 'c0c0c0' : 'ffffff',
                }
            }}
            titles={{
                title: i18n.accessContact,
                edit: i18n.editAccessibility,
                new: i18n.newAccessibility,
            }}
            headers={headers}
            dialogContent={LithologyDialogContent}
            onClick={({ contactCode }) => onShowContact(contactCode)}
            orderFunction={data => orderBy(data, ['endDate', 'order', 'startDate'])}
        />
    )
}

StationAccessibilitiesPanel.propTypes = {
    station: PropTypes.oneOfType([
        ProductionUnitShape,
        PropTypes.instanceOf(DtoDistributionUnit),
        PropTypes.instanceOf(DtoQualitometer),
        PropTypes.instanceOf(DtoPiezometer),
        PropTypes.instanceOf(DtoHydrometricStation),
        PropTypes.instanceOf(DtoInstallation),
    ]),
    readMode: PropTypes.bool,
    onChange: PropTypes.func,
    onShowContact: PropTypes.func,
}

export default StationAccessibilitiesPanel